import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import QuestionImage from '../components/ui/QuestionImage';
import QuestionText from '../components/ui/QuestionText';
import AnalyticsCard from '../components/testAnalytics/AnalyticsCard';
import LeaderboardTable from '../components/testAnalytics/LeaderboardTable';
import ScoreDistributionChart from '../components/testAnalytics/ScoreDistributionChart';
import AccuracyChart from '../components/testAnalytics/AccuracyChart';
import CompletionChart from '../components/testAnalytics/CompletionChart';
import './TestDetail.css';

const TestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [results, setResults] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState('');

  useEffect(() => {
    fetchTestDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'results') {
      fetchResults();
    }
  }, [id, activeTab]);

  const fetchTestDetails = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await API.get(`/tests/${id}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        setFetchError('Test not found or access denied.');
      } else {
        setFetchError('An error occurred while fetching test details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      setResultsLoading(true);
      setResultsError('');
      const res = await API.get(`/tests/${id}/results`);
      setResults(res.data.leaderboard || []);
      setAnalytics(res.data.analytics || null);
    } catch (err) {
      console.error(err);
      setResultsError('Unable to load leaderboard data');
    } finally {
      setResultsLoading(false);
    }
  };

  if (loading) return <div className="td-loading">Loading test details...</div>;
  if (fetchError) return <div className="td-loading" style={{color: '#dc2626'}}>{fetchError}</div>;
  if (!data || !data.test) return <div className="td-loading">Test data is unavailable.</div>;

  const { test, questions, assignments } = data;
  const isEditable = (test.completed_count || 0) === 0;
  const derivedStatus = test.is_published ? (test.completed_count > 0 && test.completed_count >= (test.assigned_count || 1) ? 'COMPLETED' : 'ACTIVE') : 'DRAFT';
  const statusColor = derivedStatus === 'COMPLETED' ? '#4f46e5' : derivedStatus === 'ACTIVE' ? '#059669' : '#334155';
  const statusBg = derivedStatus === 'COMPLETED' ? '#ecebfd' : derivedStatus === 'ACTIVE' ? '#e5f4ef' : '#f1f5f9';
  const settings = test.assessment_settings || {};
  const batchAssignments = assignments?.filter(a => a.target_type === 'batch') || [];
  const studentAssignments = assignments?.filter(a => a.target_type === 'student') || [];

  return (
    <div className="test-detail-container">
      <div className="td-header-bar">
        <div className="td-header-info">
          <button className="back-btn" onClick={() => navigate('/admin/tests')}><Icon name="chevron-left" size={16} /> Back to Tests</button>
          <h2>{test.test_name}</h2>
          <div className="td-meta-tags">
            <span className="td-tag">{test.test_type}</span>
            <span className="td-tag" style={{background: statusBg, color: statusColor, fontWeight: 600}}>
              {derivedStatus}
            </span>
            <span className="td-tag"><Icon name="timer" size={13} /> {test.duration_minutes} mins</span>
            <span className="td-tag">
              Assigned: {test.assigned_count || 0} | Completed: {test.completed_count || 0}
            </span>
          </div>
        </div>
        <div className="td-header-actions">
          <button 
            className="td-btn outline" 
            onClick={() => navigate(`/admin/tests/edit/${test.id}`)}
            disabled={!isEditable}
            title={isEditable ? 'Edit Test' : 'Cannot edit test with submitted attempts'}
            style={{ opacity: isEditable ? 1 : 0.5, cursor: isEditable ? 'pointer' : 'not-allowed' }}
          >
            <Icon name="edit" size={16} /> Edit Test
          </button>
        </div>
      </div>

      <div className="td-tabs">
        <button className={`td-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`td-tab ${activeTab === 'questions' ? 'active' : ''}`} onClick={() => setActiveTab('questions')}>Questions ({questions?.length || 0})</button>
        <button className={`td-tab ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>Results & Analytics</button>
      </div>

      {activeTab === 'overview' && (
        <div className="td-overview-grid">
          {/* Test Details Card */}
          <div className="td-card">
            <h3>Test Details</h3>
            <div className="td-info-list">
              <div className="td-info-item">
                <span>Description</span>
                <strong>{settings.description || 'No description provided'}</strong>
              </div>
              <div className="td-info-item">
                <span>Passing Percentage</span>
                <strong>{settings.passing_percentage || 60}%</strong>
              </div>
              <div className="td-info-item">
                <span>Total Questions</span>
                <strong>{questions?.length || 0}</strong>
              </div>
              <div className="td-info-item">
                <span>Total Marks</span>
                <strong>{test.total_marks || test.question_count || 0}</strong>
              </div>
              <div className="td-info-item">
                <span>Schedule</span>
                <strong>{test.scheduled_at ? new Date(test.scheduled_at).toLocaleString() : 'Instant / Anytime'}</strong>
              </div>
            </div>
          </div>

          {/* Assessment Settings Card */}
          <div className="td-card">
            <h3>Assessment Settings</h3>
            <div className="td-info-list">
              <div className="td-info-item">
                <span>Randomize Questions</span>
                <strong>{settings.randomize_questions ? 'Yes' : 'No'}</strong>
              </div>
              <div className="td-info-item">
                <span>Negative Marking</span>
                <strong>{settings.negative_marking ? 'Yes' : 'No'}</strong>
              </div>
              <div className="td-info-item">
                <span>Tab Switch Detection</span>
                <strong>{settings.tab_switch_detection ? 'Enabled' : 'Disabled'}</strong>
              </div>
              <div className="td-info-item">
                <span>Fullscreen Enforced</span>
                <strong>{settings.fullscreen_mode ? 'Enabled' : 'Disabled'}</strong>
              </div>
            </div>
          </div>

          {/* Audience Summary Card */}
          <div className="td-card">
            <h3>Audience Summary</h3>
            <div className="td-info-list">
              <div className="td-info-item">
                <span>Assigned Batches</span>
                <strong>{batchAssignments.length}</strong>
              </div>
              <div className="td-info-item">
                <span>Individually Assigned Students</span>
                <strong>{studentAssignments.length}</strong>
              </div>
              <div className="td-info-item">
                <span>Total Students Reached</span>
                <strong>{test.assigned_count || 0}</strong>
              </div>
              <div className="td-info-item">
                <span>Tests Completed</span>
                <strong>{test.completed_count || 0}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'questions' && (
        <div className="td-questions-view" style={{marginTop: '20px'}}>
          {(!questions || questions.length === 0) ? (
            <div className="td-card" style={{textAlign: 'center', padding: '40px', color: '#64748b'}}>
              No questions found for this test.
            </div>
          ) : (
            <div className="td-card">
              <h3>Test Questions ({questions.length})</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px'}}>
                {questions.map((q, idx) => (
                  <div key={q.id || idx} style={{padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'var(--z-card-alt)'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                      <strong>Q{idx + 1}</strong>
                      <span style={{fontSize: '0.8rem', padding: '2px 8px', borderRadius: '4px', background: '#e2e8f0', textTransform: 'capitalize'}}>{q.difficulty || 'Normal'}</span>
                    </div>
                    <div style={{margin: '0 0 12px 0', color: '#334155'}}><QuestionText text={q.question_text} /></div>
                    <QuestionImage path={q.image_path} compact />
                    {q.options && q.options.length > 0 && (
                      <ul style={{listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '6px'}}>
                        {q.options.map((opt, oIdx) => (
                          <li key={oIdx} style={{padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', fontSize: '0.9rem', color: (String(opt).trim().toLowerCase() === String(q.correct_answer).trim().toLowerCase()) ? '#059669' : '#334155', fontWeight: (String(opt).trim().toLowerCase() === String(q.correct_answer).trim().toLowerCase()) ? 600 : 400, borderColor: (String(opt).trim().toLowerCase() === String(q.correct_answer).trim().toLowerCase()) ? '#e5f4ef' : '#e2e8f0'}}>
                            {opt} {(String(opt).trim().toLowerCase() === String(q.correct_answer).trim().toLowerCase()) && <Icon name="check" size={14} />}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'results' && (
        <div className="ta-dashboard">
          {resultsLoading ? (
            <div className="td-loading">Loading analytics...</div>
          ) : resultsError ? (
            <div className="td-loading" style={{color: '#dc2626'}}>{resultsError}</div>
          ) : (
            <>
              {/* TOP SUMMARY CARDS */}
              {analytics && (
                <div className="ta-summary-grid">
                  <AnalyticsCard title="Average Score" value={`${analytics.average_percentage ?? 0}%`} icon="mission" />
                  <AnalyticsCard title="Average Accuracy" value={`${analytics.average_accuracy}%`} icon="success" />
                  <AnalyticsCard title="Completion Rate" value={`${analytics.completion_rate}%`} icon="trend-up" />
                  <AnalyticsCard title="Avg Time" value={analytics.average_completion_time} icon="timer" />
                </div>
              )}

              {/* CHARTS ROW */}
              <div className="ta-charts-grid">
                <ScoreDistributionChart results={results} totalMarks={test.total_marks || test.question_count || 1} />
                <AccuracyChart results={results} />
                <CompletionChart results={results} />
              </div>

              {/* LEADERBOARD TABLE */}
              <LeaderboardTable results={results} totalMarks={test.total_marks || test.question_count || 1} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TestDetail;
